import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { theme } from "uikit";

import { Diagnostics } from "../../../../../../services";
import Panel, {
	EmptyState,
	PanelHint,
	PanelTitle,
} from "../../components/Panel";
import DataTable, { TableScroll } from "../../components/DataTable";
import { useSystemPerformance } from "../../context";
import useRequest from "../../hooks/useRequest";
import { formatTime } from "../../utils/format";

const Root = styled.div`
	display: flex;
	flex-direction: column;
	gap: 16px;
	min-width: 0;
	max-width: 100%;
`;

const Channels = styled.div`
	display: flex;
	flex-wrap: wrap;
	gap: 4px 8px;
	max-width: 480px;
	white-space: normal;
`;

const Channel = styled.span`
	font-size: 12px;
	line-height: 1.3;
	opacity: 0.85;
`;

const Details = styled.details`
	min-width: 0;

	summary {
		cursor: pointer;
		user-select: none;
		color: ${theme.colors.primary};
	}
`;

function formatUser(
	client: Diagnostics.ClientSession,
	anonymousLabel: string,
): string {
	if (!client.userType && !client.userId) return anonymousLabel;
	if (client.userType && client.userId) {
		return `${client.userType}-${client.userId}`;
	}
	return client.userType || client.userId;
}

const Clients: React.FC = () => {
	const { t } = useTranslation();
	const { refreshToken } = useSystemPerformance();
	const [expanded, setExpanded] = useState<Record<string, boolean>>({});

	const { data } = useRequest<Diagnostics.ClientsResponse>(
		() => Diagnostics.getClients({ limit: 200 }),
		[refreshToken],
	);

	return (
		<Root>
			<Panel>
				<PanelTitle>
					{t("settings.systemPerformance.clients.title")}
				</PanelTitle>
				<PanelHint>
					{t("settings.systemPerformance.clients.hint")}
				</PanelHint>
				{data ? (
					<PanelHint>
						{t("settings.systemPerformance.clients.summary", {
							clients: data.totalClients,
							subscriptions: data.totalSubscriptions,
							pod: data.pod,
						})}
					</PanelHint>
				) : null}

				{data?.items?.length ? (
					<TableScroll>
						<DataTable>
							<thead>
								<tr>
									<th>
										{t(
											"settings.systemPerformance.clients.user",
										)}
									</th>
									<th>
										{t(
											"settings.systemPerformance.clients.ip",
										)}
									</th>
									<th>
										{t(
											"settings.systemPerformance.clients.sessionId",
										)}
									</th>
									<th>
										{t(
											"settings.systemPerformance.clients.connectedAt",
										)}
									</th>
									<th className="numeric">
										{t(
											"settings.systemPerformance.clients.subscriptionCount",
										)}
									</th>
									<th>
										{t(
											"settings.systemPerformance.clients.subscriptions",
										)}
									</th>
								</tr>
							</thead>
							<tbody>
								{data.items.map((client) => {
									const isOpen = expanded[client.sessionId];
									const channels = client.subscriptions;
									const preview = channels.slice(0, 4);
									const rest = channels.length - preview.length;

									return (
										<tr key={client.sessionId}>
											<td>
												{formatUser(
													client,
													t(
														"settings.systemPerformance.clients.anonymous",
													) ?? "",
												)}
											</td>
											<td>
												{client.remoteAddress || "—"}
											</td>
											<td title={client.sessionId}>
												{client.sessionId.slice(0, 8)}…
											</td>
											<td>
												{formatTime(client.connectedAt)}
											</td>
											<td className="numeric">
												{client.subscriptionCount}
											</td>
											<td className="wide">
												{channels.length ? (
													<Details
														open={isOpen}
														onToggle={(event) => {
															const next = (
																event.target as HTMLDetailsElement
															).open;
															setExpanded(
																(state) => ({
																	...state,
																	[client.sessionId]:
																		next,
																}),
															);
														}}
													>
														<summary>
															{isOpen
																? t(
																		"settings.systemPerformance.clients.subscriptions",
																  )
																: preview
																		.map(
																			(
																				item,
																			) =>
																				item.count >
																				1
																					? `${item.name}×${item.count}`
																					: item.name,
																		)
																		.join(
																			", ",
																		) +
																  (rest > 0
																		? ` +${rest}`
																		: "")}
														</summary>
														<Channels>
															{channels.map(
																(item) => (
																	<Channel
																		key={
																			item.name
																		}
																	>
																		{item.count >
																		1
																			? `${item.name}×${item.count}`
																			: item.name}
																	</Channel>
																),
															)}
														</Channels>
													</Details>
												) : (
													t(
														"settings.systemPerformance.clients.noSubscriptions",
													)
												)}
											</td>
										</tr>
									);
								})}
							</tbody>
						</DataTable>
					</TableScroll>
				) : (
					<EmptyState>
						{t("settings.systemPerformance.clients.empty")}
					</EmptyState>
				)}
			</Panel>
		</Root>
	);
};

export default Clients;
